import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useDispatch } from "react-redux";

import PreneedProfileView from "./components/PreneedProfileView";
import PreneedProfileForm from "./components/PreneedProfileForm";
import SecuritySection from "./SecuritySection";
import ChangePasswordModal from "./ChangePasswordModal";

import ProfileHeader from "../../../common/components/profile-header/profile-header-and-breadcrumbs";
import AlertComponent from "../../../common/components/alert-component/alert-component";

import { getPreneedProfileDetails } from "../../../api/preneed-section/preneed-profile.api";
import { userLogout } from "../../../api/auth";
import { logoutByUserType, updateSessionDetails } from "../../../common/routines/user-operations";
import { getCurrentUserDetails } from "../../../common/routines/user-operations";
import LinkPartnerSection from "./LinkPartnerSection";

import "./preneed-profile.scss";

type PreneedProfileDetails = {
  first_name?: string;
  last_name?: string;
  email?: string;
  phoneNumber?: string;
  preneedId?: string | number;
  preneed_id?: string | number;
  profile_photo?: string | null;
  address?: {
    id?: string | number;
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    zipcode?: string;
  } | null;
};

const normalizePreneedProfile = (
  apiProfile: any | null,
  auth: any | null
) => {
  const p = apiProfile ?? {};
  const user = auth?.user ?? {};
  const planner = auth?.planner ?? {};

  const fullName =
    p.fullName ??
    [p.first_name, p.last_name].filter(Boolean).join(" ") ??
    [user.first_name, user.last_name].filter(Boolean).join(" ") ??
    "";

  const businessEmail = p.businessEmail ?? p.email ?? user.email ?? "";

  const phoneNumber =
    p.phoneNumber ?? p.mobileNumber ?? user.mobileNumber ?? "";

  // IMPORTANT: your auth object clearly has planner_id
  const preneedId =
    p.preneedId ??
    p.preneed_id ??
    planner.planner_id ??
    planner.plannerId ??
    "";

  const profile_photo =
    p.profile_photo ??
    p.profilePhoto ??
    p.profileImage ??
    user.avatarUrl ??
    null;

  const address = p.address ?? null;

  return {
    ...p,
    fullName,
    businessEmail,
    phoneNumber,
    preneedId,
    preneed_id: p.preneed_id ?? preneedId,
    profile_photo,
    address,
  };
};


const PreneedProfilePage: React.FC = () => {
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const dispatch = useDispatch();

  const [preneedProfileDetails, setPreneedProfileDetails] =
    useState<PreneedProfileDetails | null>(null);

  const [openChangePwd, setOpenChangePwd] = useState(false);

  // ✅ alert state
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<
    "success" | "error" | "info" | "warning"
  >("success");

  const breadcrumbLinkForList = [
    {
      label: "Preneed Profile",
      link: "#",
      handleClick: () => { },
    },
  ];

  const isProfileCreated = Boolean(
    getCurrentUserDetails('preNeed')?.planner?.planner_id
  );

  const preneedAuth = getCurrentUserDetails("preNeed");

  console.log('testingggg', getCurrentUserDetails('preNeed'));

  const fetchPreneedProfileDetails = async () => {
    try {
      setLoading(true);

      const response = await getPreneedProfileDetails();

      if (response?.data?.success) {
        const profileData = response.data.data;
        setPreneedProfileDetails(profileData);

        // sync with header
        updateSessionDetails("preNeed", {
          ...profileData,
          first_name:
            profileData.first_name || profileData.fullName?.split(" ")[0],
          last_name:
            profileData.last_name ||
            profileData.fullName?.split(" ").slice(1).join(" "),
          profile_photo:
            profileData.profile_photo ||
            profileData.profileImage ||
            profileData.profilePhoto,
        });
      }
    } catch (error) {
      console.error("PRENEED PROFILE GET FAILED::", error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchPreneedProfileDetails();
  }, []);

  const handlePreneedLogoutAfterPasswordChange = async () => {
    try {
      const response = await userLogout();
      if (response && response.status === 200) {
        logoutByUserType("preNeed");
      } else {
        logoutByUserType("preNeed");
      }
    } catch (err) {
      console.error("PRENEED LOGOUT FAILED::", err);
      logoutByUserType("preNeed");
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box className="preneed-profile-page__spinner-container">
          <CircularProgress size="3rem" />
        </Box>
      );
    }

    if (!isEditMode) {
      return (
        <>
          <PreneedProfileView
            isEditMode={isEditMode}
            onEdit={() => setIsEditMode(true)}
            preneedProfileDetails={preneedProfileDetails}
          />

          <SecuritySection onChangePassword={() => setOpenChangePwd(true)} />
            <LinkPartnerSection
            providerProfileDetails={preneedProfileDetails}
            onSuccess={() => {
              setAlertMessage("Partner linked successfully");
              setAlertType("success");
              setAlertOpen(true);
              fetchPreneedProfileDetails();
            }}
            onError={(msg) => {
              setAlertMessage(msg);
              setAlertType("error");
              setAlertOpen(true);
            }}
          />
        </>
      );
    }

    return (
      <>
        <PreneedProfileForm
          isProfileCreated={isProfileCreated}
          preneedProfileDetails={preneedProfileDetails}
          setPreneedProfileDetails={setPreneedProfileDetails}
          setIsEditMode={setIsEditMode}
          onCancel={() => setIsEditMode(false)}
          onSave={() => setIsEditMode(false)}
          fetchPreneedProfileDetails={fetchPreneedProfileDetails}
        />

        <SecuritySection onChangePassword={() => setOpenChangePwd(true)} />
          <LinkPartnerSection
            providerProfileDetails={preneedProfileDetails}
            onSuccess={() => {
              setAlertMessage("Partner linked successfully");
              setAlertType("success");
              setAlertOpen(true);
              fetchPreneedProfileDetails();
            }}
            onError={(msg) => {
              setAlertMessage(msg);
              setAlertType("error");
              setAlertOpen(true);
            }}
          />
      </>
    );
  };

  return (
    <div className="preneed-profile-main-wrapper listing-page-main-wrapper">
      {/* ✅ Header: same pattern as ServiceList */}
      <ProfileHeader mode="preNeed" breadcrumbLink={breadcrumbLinkForList} />

      {/* ✅ Content wrapper: same padding pattern as ServiceList */}
      <div className="preneed-profile-content-wrapper">
        {renderContent()}
      </div>

      <ChangePasswordModal
        open={openChangePwd}
        onClose={() => setOpenChangePwd(false)}
        onSuccess={() => {
          setOpenChangePwd(false);

          setAlertType("success");
          setAlertMessage("Password has been changed");
          setAlertOpen(true);

          setTimeout(() => {
            handlePreneedLogoutAfterPasswordChange();
          }, 1200);
        }}
      />

      <AlertComponent
        open={alertOpen}
        type={alertType}
        message={alertMessage}
        onClose={() => setAlertOpen(false)}
        autoHideDuration={3000}
      />
    </div>
  );
};

export default PreneedProfilePage;
